import { action, json, redirect } from '@solidjs/router';
import { decode } from 'decode-formdata';
import { EndpointByMethod } from './__generated__/validators';
import { revalidate } from './cache';
import { ApiError, UnknownApiError } from './errors';
import { getApiClient } from './client';
import type { CacheablePaths } from './cache';
import type { Action } from '@solidjs/router';
import type { paths } from './__generated__/database';
import type { FetchOptions, FetchResponse, MaybeOptionalInit, RequestOptions } from 'openapi-fetch';
import type { FilterKeys, PathsWithMethod } from 'openapi-typescript-helpers';

type DeepPartial<T> = T extends object
	? {
			[P in keyof T]?: DeepPartial<T[P]>;
		}
	: T;
type Options<Res extends Record<string, unknown>, Input> = {
	revalidate?:
		| CacheablePaths
		| Array<CacheablePaths>
		| ((data: Res, input: FormData) => Array<CacheablePaths> | undefined);
	redirect?: (data: Res, input: FormData) => undefined | string;
	onSuccess?: (data: Res, input: FormData) => Promise<void>;
	transformer?: (input: FormData) => DeepPartial<Input>;
};

export function clientAction<
	Method extends 'get' | 'patch' | 'post' | 'put' | 'delete',
	Path extends PathsWithMethod<paths, Method>,
	Res extends NonNullable<
		Exclude<
			FetchResponse<Required<paths[Path][Method]>, MaybeOptionalInit<paths[Path], Method>, 'application/json'>['data'],
			'undefined'
		>
	>,
	Rest extends Omit<FetchOptions<FilterKeys<paths[Path], Method>>, 'body'> | undefined,
>(
	method: Method,
	path: Path,
	rest?: Rest | ((data: FormData) => Rest),
	opts?: Options<NonNullable<Res>, FetchOptions<FilterKeys<paths[Path], Method>>['body']>,
	key?: string,
): Action<[FormData], NonNullable<Res>> {
	const req = makeRequest(method, path, rest, opts);
	return action(
		async (data: FormData) => {
			const res = await req(data);
			return json(res, { revalidate: 'nothing' });
		},
		key ?? `${method}-${path}`,
	) as Action<[FormData], NonNullable<Res>>;
}

export function makeRequest<
	Method extends 'get' | 'patch' | 'post' | 'put' | 'delete',
	Path extends PathsWithMethod<paths, Method>,
	Res extends NonNullable<
		Exclude<
			FetchResponse<Required<paths[Path][Method]>, MaybeOptionalInit<paths[Path], Method>, 'application/json'>['data'],
			'undefined'
		>
	>,
	Rest extends Omit<FetchOptions<FilterKeys<paths[Path], Method>>, 'body'> | undefined,
>(
	method: Method,
	path: Path,
	rest?: Rest | ((data: FormData) => Rest),
	opts?: Options<NonNullable<Res>, FetchOptions<FilterKeys<paths[Path], Method>>['body']>,
): (data: FormData) => Promise<Res> {
	return async (data: FormData) => {
		const client = getApiClient();
		const isFormData = data instanceof FormData;
		const isSearchData = data instanceof URLSearchParams;
		const tempDecoded = (isFormData || isSearchData ? decode(data as FormData) : data) as Record<string, unknown>;

		const transformed = opts?.transformer
			? opts.transformer(data instanceof URLSearchParams ? (data as FormData) : new FormData())
			: {};

		const reqData = {
			...tempDecoded,
			...transformed,
			// @ts-expect-error overloading a lot here
			query: { ...tempDecoded?.query, ...tempDecoded, ...transformed },
			// @ts-expect-error overloading a lot here
			body: { ...tempDecoded?.body, ...tempDecoded, ...transformed },
		};

		// @ts-expect-error overloading a lot here
		const validator = EndpointByMethod[method][path]['parameters'];
		const reqInit = validator?.safeParse(reqData)?.data ?? reqData;

		const requestInit = {
			body: reqInit.body,
			headers: reqInit.header,
			params: {
				path: reqInit.path ?? {},
				query: reqInit.query ?? {},
			},
		} as unknown as RequestOptions<FilterKeys<paths[Path], Method>>;

		// @ts-expect-error TS doesn't want you to be clever
		const res = await client[method.toUpperCase()](path, {
			...requestInit,
			...(typeof rest === 'function' ? rest(data) : rest),
		} as FetchOptions<FilterKeys<paths[Path], Method>>);

		if (res.error) {
			throw new ApiError(res.response.status, res.error);
		}

		if (res.response.status !== 204 && !res.data) {
			throw new UnknownApiError();
		}

		const resData = res.data ?? {};

		const revalidateData = typeof opts?.revalidate === 'function' ? opts.revalidate(resData, data) : opts?.revalidate;

		if (revalidateData || 'revalidate' in (opts ?? {})) {
			await revalidate(revalidateData, true);
		}

		// Important: run success _after_ revalidate completes
		if (opts?.onSuccess) {
			await opts.onSuccess(resData, data);
		}

		const redirectUrl = opts?.redirect ? opts.redirect(resData, data) : undefined;
		if (redirectUrl) {
			await new Promise<void>((resolve) => {
				setTimeout(() => resolve(), 100);
			});
			throw redirect(redirectUrl);
		}

		return resData;
	};
}
